// --- Dependencies -------------------------------------------------------------------------------
import { useEffect } from "react";
import { Link } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import usePermission from "@/hooks/usePermission";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse, TyLogSeverity, TyStoreAnalyticsResponse } from "@/types";
import BarChart from "@/UI/atoms/BarChart";
import MeterList from "@/UI/atoms/MeterList";
import SeverityBar from "@/UI/atoms/SeverityBar";
import Skeleton from "@/UI/atoms/Skeleton";
import { formatBucket, formatDateTime, formatDuration } from "../LogsScreen/helpers";

// --- Types --------------------------------------------------------------------------------------

type Props = { storeId: string };

// --- Constants ----------------------------------------------------------------------------------

/** Matches the retention window, so the range control can never ask for data that no longer exists. */
const DAYS = 7;

// --- Logic --------------------------------------------------------------------------------------

/**
 * How hard this store leans on the system and what broke while it did. Fetched separately from the
 * store itself so the rest of the screen still lands immediately — this is the slowest query here,
 * and it is the least urgent thing on the page.
 */
export default function ActivitySection({ storeId }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const { canView } = usePermission();

    const { data, loading, error, invoke } = useFetch<TyApiResponse<TyStoreAnalyticsResponse>>(
        `stores/${storeId}/analytics`,
        { method: "GET" },
        false
    );

    useEffect(() => {
        invoke({ query: { days: DAYS } });
    }, [storeId]);

    const stats = data?.data;

    return (
        <section className="one-store-section">
            <div className="one-store-activity-head">
                <p className="one-store-section-title">{t.storeDetail.sectionActivity}</p>
                <span className="one-store-activity-range">{t.storeDetail.activityRange.replace("{days}", String(stats?.days ?? DAYS))}</span>
            </div>

            {loading && <Skeleton className="one-store-skeleton-section" />}

            {!loading && error && <p className="one-store-activity-empty">{error}</p>}

            {!loading && !error && stats && stats.totals.total === 0 && <p className="one-store-activity-empty">{t.storeDetail.activityEmpty}</p>}

            {!loading && !error && stats && stats.totals.total > 0 && (
                <>
                    <div className="one-store-stats">
                        {(
                            [
                                [t.storeDetail.activityRequests, stats.totals.total.toLocaleString(), false],
                                [t.storeDetail.activityErrors, stats.totals.errors.toLocaleString(), stats.totals.errors > 0],
                                [t.storeDetail.activityErrorRate, `${stats.totals.errorRate}%`, stats.totals.errors > 0],
                                [t.storeDetail.activityAvgResponse, formatDuration(stats.totals.avgResponseTime), false],
                            ] as const
                        ).map(([label, value, isBad]) => (
                            <div key={label} className="one-store-stat" data-tone={isBad ? "bad" : "plain"}>
                                <p className="one-store-stat-value">{value}</p>
                                <p className="one-store-stat-label">{label}</p>
                            </div>
                        ))}
                    </div>

                    <div className="one-store-activity-grid">
                        <div className="one-store-activity-panel">
                            <p className="one-store-activity-panel-title">{t.storeDetail.activityPerDay}</p>
                            <BarChart
                                points={stats.series.map((point) => ({
                                    label: formatBucket(point.at, "day", lang),
                                    total: point.total,
                                    errors: point.errors,
                                    rejected: point.rejected,
                                }))}
                                emptyLabel={t.storeDetail.activityEmpty}
                                describe={(point) => `${point.label} · ${point.total} · ${t.enums.logSeverity.error} ${point.errors ?? 0}`}
                            />
                        </div>

                        <div className="one-store-activity-panel">
                            <p className="one-store-activity-panel-title">{t.storeDetail.activityBySurface}</p>
                            <MeterList
                                rows={stats.bySurface.map((row) => ({
                                    key: row.key,
                                    label: t.enums.logSurface[row.key as keyof typeof t.enums.logSurface] ?? row.key,
                                    value: row.value,
                                }))}
                                emptyLabel={t.storeDetail.activityEmpty}
                            />
                        </div>

                        <div className="one-store-activity-panel">
                            <p className="one-store-activity-panel-title">{t.logs.bySeverity}</p>
                            <SeverityBar
                                emptyLabel={t.storeDetail.activityEmpty}
                                segments={(["success", "rejected", "error"] as TyLogSeverity[]).map((key) => ({
                                    key,
                                    label: t.enums.logSeverity[key],
                                    value: key === "error" ? stats.totals.errors : key === "rejected" ? stats.totals.rejected : stats.totals.success,
                                    tone: key === "error" ? ("bad" as const) : key === "rejected" ? ("warn" as const) : ("good" as const),
                                }))}
                            />
                        </div>
                    </div>

                    {stats.topFailing.length > 0 && (
                        <div className="one-store-activity-panel">
                            <p className="one-store-activity-panel-title">{t.storeDetail.activityTopFailing}</p>
                            <div className="one-store-activity-failures">
                                {stats.topFailing.map((row) => (
                                    <div key={`${row.route}-${row.message}`} className="one-store-activity-failure">
                                        <div className="one-store-activity-failure-main">
                                            <p className="one-store-activity-failure-route" dir="ltr">
                                                {row.route}
                                            </p>
                                            {row.message && <p className="one-store-activity-failure-message">{row.message}</p>}
                                            <p className="one-store-activity-failure-seen">
                                                {t.storeDetail.activityLastSeen} {formatDateTime(row.lastAt, lang)}
                                            </p>
                                        </div>
                                        <span className="one-store-activity-failure-count">{row.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Only offered to admins who can actually open the logs section. */}
                    {canView("logs") && (
                        <Link to={`/logs?store=${storeId}`} className="one-store-activity-link">
                            {t.storeDetail.activityOpenLogs}
                        </Link>
                    )}
                </>
            )}
        </section>
    );
}
